/** Usage Rules — guidelines for working with the learner design system. */
import { SURFACE, eyebrow, body, Section } from '../shared';

// ─── Rule card ────────────────────────────────────────────────────────────────

function Rule({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div style={{ ...SURFACE, padding: 20, marginBottom: 12 }}>
      <p style={{ ...eyebrow, color: 'var(--color-accent)', marginBottom: 8 }}>{heading}</p>
      <div style={body}>{children}</div>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function UsageTab() {
  return (
    <>
      <Section label="Token Contract">
        <Rule heading="Use semantic tokens">
          <p>
            Learner components must use scoped semantic tokens such as{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              --color-* / --font-* / --space-* / --type-*
            </code>{' '}
            . These are defined in{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              interfaces/learner/theme/tokens.css
            </code>{' '}
            and can be changed freely without affecting workspace or session-player UI. Internal
            seed variables should stay inside the token file.
          </p>
        </Rule>
        <Rule heading="Never import learner code from other interfaces">
          <p>
            Learner pages import components from{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              @/interfaces/learner/ui
            </code>
            . They must not import{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              @/interfaces/workspace/ui
            </code>{' '}
            or{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              @/interfaces/session-player/ui
            </code>
            .
          </p>
        </Rule>
      </Section>

      <Section label="Surface Hierarchy">
        <Rule heading="Three levels">
          <p style={{ marginBottom: 8 }}>
            1. <strong>Background</strong> —{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              --color-bg
            </code>
            , the page canvas. Matte, warm-tinted.
          </p>
          <p style={{ marginBottom: 8 }}>
            2. <strong>Surface</strong> —{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              --color-surface
            </code>
            , translucent panels with blur + inner highlight. Cards, containers, panels.
          </p>
          <p>
            3. <strong>Raised</strong> —{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              --color-surface-solid
            </code>
            , solid white for active elements (selected tabs, elevated controls).
          </p>
        </Rule>
      </Section>

      <Section label="Component Patterns">
        <Rule heading="Pill shape for inline controls">
          <p>
            Buttons, chips, stat pills, and tabs all use{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              border-radius: 9999px
            </code>
            . Cards and panels use{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              14px
            </code>
            .
          </p>
        </Rule>
        <Rule heading="Inner highlight on glass surfaces">
          <p>
            Every glass surface gets{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              inset 0 0.5px 0 var(--color-surface-highlight)
            </code>{' '}
            in the box-shadow to simulate a top-edge light reflection.
          </p>
        </Rule>
        <Rule heading="Accent bar on cards">
          <p>
            Content cards have a 2px coloured bar at the top. This is the only use of hard colour on
            card surfaces.
          </p>
        </Rule>
        <Rule heading="No card wrappers for content sections">
          <p>
            Don&apos;t wrap content sections in card-like containers. Cards are for interactive
            items — courses, lessons, metrics. Sections flow directly on the background.
          </p>
        </Rule>
      </Section>

      <Section label="Interaction">
        <Rule heading="Hover style">
          <p>
            Interactive cards darken their border to{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              --color-border-strong
            </code>{' '}
            with a subtle shadow elevation on hover. No translateY lift — elevation is communicated
            through border and shadow only.
          </p>
        </Rule>
        <Rule heading="Transition timing">
          <p>
            All hover transitions use{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              0.15s
            </code>{' '}
            for colour/shadow changes. Card lifts use{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              0.2s
            </code>
            .
          </p>
        </Rule>
      </Section>

      <Section label="Session Player">
        <Rule heading="Lives in its own interface now">
          <p>
            Session pages (
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              features/session-player/
            </code>
            ) now sit behind the{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              session-player
            </code>{' '}
            boundary and import through{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                background: 'var(--color-surface-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              @/interfaces/session-player/ui
            </code>{' '}
            rather than the learner UI library.
          </p>
        </Rule>
      </Section>
    </>
  );
}
